/** Amministrazione → Modelli programma (AUD-F2.14): modello Word del programma di audit, versionato. */
import { useState } from "react";
import { Alert, Button, Card, Input, Space, Tag, Upload } from "antd";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import type { ProgrammeTemplateDto } from "@audit-plan/shared";
import { activateProgrammeTemplate, programmeTemplateUrl, uploadProgrammeTemplate, useProgrammeTemplates } from "../../api/programme";
import { useNotify } from "../audit/useNotify";
import { fmtDT } from "../pills";
import { ResizableTable } from "../ResizableTable";
import { ProgrammeModelEditor } from "./ProgrammeModelEditor";

export function ProgrammeTemplatesTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const n = useNotify();
  const q = useProgrammeTemplates();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const rows = q.data ?? [];
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      void qc.invalidateQueries({ queryKey: ["programme-templates"] });
      n.ok();
    } catch (e) {
      n.fail(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: 16 }}>
    <ProgrammeModelEditor />
    <Card>
      <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t("pg_adminIntro")} />
      <Space wrap style={{ marginBottom: 16 }}>
        <Button icon={<DownloadOutlined />} href={programmeTemplateUrl()}>{t("pg_downloadActive")}</Button>
        <Input style={{ width: 280 }} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("pg_notePh")} maxLength={500} />
        <Upload
          accept=".docx"
          showUploadList={false}
          disabled={busy}
          beforeUpload={(file) => {
            void run(async () => {
              await uploadProgrammeTemplate(file, note.trim());
              setNote("");
            });
            return false;
          }}
        >
          <Button type="primary" icon={<UploadOutlined />} loading={busy}>{t("pg_uploadTemplate")}</Button>
        </Upload>
      </Space>
      <ResizableTable<ProgrammeTemplateDto>
        rowKey="id"
        size="small"
        loading={q.isLoading}
        dataSource={rows}
        pagination={false}
        locale={{ emptyText: t("pg_defaultOnly") }}
        columns={[
          { title: t("pg_version"), dataIndex: "v", width: 90, render: (v: number, r) => <Space>v{v}{r.active && <Tag color="green">{t("pg_active")}</Tag>}</Space> },
          { title: t("name"), dataIndex: "name" },
          { title: t("pg_note"), dataIndex: "note" },
          { title: t("pg_uploadedAt"), dataIndex: "createdAt", width: 160, render: (d: string) => fmtDT(d) },
          { title: "sha256", dataIndex: "sha256", width: 130, render: (h: string | null) => <span style={{ fontFamily: "var(--ap-mono)", fontSize: 12 }}>{h ? `${h.slice(0, 12)}…` : "—"}</span> },
          {
            title: "",
            width: 120,
            render: (_: unknown, r) => (!r.active ? <Button size="small" disabled={busy} onClick={() => void run(() => activateProgrammeTemplate(r.id))}>{t("pg_makeActive")}</Button> : null),
          },
        ]}
      />
    </Card>
    </div>
  );
}
